import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../utils/axios-auth';
const initialState = {
    posts: [],
    postComments: {},
    features: [],
    featureComments: {},
    loading: false,
    error: null,
};
// =============================
// Async Thunks for Tutor Collaboration
// =============================
export const fetchTutorPosts = createAsyncThunk('community/fetchTutorPosts', async (params = {}, { rejectWithValue }) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.tutor_id)
            queryParams.append('tutor_id', params.tutor_id);
        if (params.category)
            queryParams.append('category', params.category);
        if (params.tags)
            queryParams.append('tags', params.tags.join(','));
        const { data } = await axios.get(`/community/posts?${queryParams.toString()}`);
        return data.data;
    }
    catch (err) {
        // Fallback to public endpoint on auth errors or auth-like server errors
        const status = err.response?.status;
        const message = err.response?.data?.message || err.message || '';
        const isAuthIssue = status === 401 || status === 403 || message.toLowerCase().includes('authentication required');
        try {
            if (isAuthIssue) {
                const queryParams = new URLSearchParams();
                if (params.tutor_id)
                    queryParams.append('tutor_id', params.tutor_id);
                if (params.category)
                    queryParams.append('category', params.category);
                if (params.tags)
                    queryParams.append('tags', params.tags.join(','));
                const { data: pub } = await axios.get(`/community/posts/public?${queryParams.toString()}`);
                // Normalize possible field differences from public fallback
                const normalized = (pub.data || []).map((p) => ({
                    id: p.id,
                    tutor_id: p.tutor_id || p.tutorId || '',
                    title: p.title,
                    content: p.content,
                    category: p.category?.replace('-', '_') || 'general',
                    tags: Array.isArray(p.tags) ? p.tags : (typeof p.tags === 'string' ? p.tags.split(',').map((t) => t.trim()).filter(Boolean) : []),
                    is_public: p.is_public !== false,
                    created_at: p.created_at || p.createdAt || new Date().toISOString(),
                    updated_at: p.updated_at || p.updatedAt || p.created_at || new Date().toISOString(),
                    like_count: p.like_count ?? p.likes_count ?? 0,
                    comment_count: p.comment_count ?? p.comments_count ?? 0,
                    liked: Boolean(p.liked)
                }));
                return normalized;
            }
        }
        catch (fallbackErr) {
            return rejectWithValue(fallbackErr.response?.data?.message || fallbackErr.message);
        }
        return rejectWithValue(message);
    }
});
export const createTutorPost = createAsyncThunk('community/createTutorPost', async ({ title, content, category = 'general', tags = [], is_public = true }, { rejectWithValue }) => {
    try {
        const { data } = await axios.post('/community/posts', { title, content, category, tags, is_public });
        return data.data;
    }
    catch (err) {
        return rejectWithValue(err.response?.data?.message || err.message);
    }
});
export const togglePostLike = createAsyncThunk('community/togglePostLike', async ({ post_id }, { rejectWithValue }) => {
    try {
        const { data } = await axios.post('/community/posts/like', { post_id });
        return { post_id, liked: data.data.liked, like_count: data.data.like_count };
    }
    catch (err) {
        return rejectWithValue(err.response?.data?.message || err.message);
    }
});
export const addPostComment = createAsyncThunk('community/addPostComment', async ({ post_id, content }, { rejectWithValue }) => {
    try {
        const { data } = await axios.post('/community/posts/comment', { post_id, content });
        return data.data;
    }
    catch (err) {
        return rejectWithValue(err.response?.data?.message || err.message);
    }
});
export const fetchPostComments = createAsyncThunk('community/fetchPostComments', async ({ post_id }, { rejectWithValue }) => {
    try {
        const { data } = await axios.get(`/community/posts/${post_id}/comments`);
        return { post_id, comments: data.data };
    }
    catch (err) {
        return rejectWithValue(err.response?.data?.message || err.message);
    }
});
// =============================
// Async Thunks for Feature Voting (Secondary)
// =============================
export const fetchTutorFeatures = createAsyncThunk('community/fetchTutorFeatures', async (_, { rejectWithValue }) => {
    try {
        const { data } = await axios.get('/community/features');
        return data.data;
    }
    catch (err) {
        // Fallback to public endpoint on auth errors or auth-like server errors
        const status = err.response?.status;
        const message = err.response?.data?.message || err.message || '';
        const isAuthIssue = status === 401 || status === 403 || message.toLowerCase().includes('authentication required');
        try {
            if (isAuthIssue) {
                const { data: pub } = await axios.get('/community/features/public');
                // Normalize possible field differences from public fallback
                const normalized = (pub.data || []).map((f) => ({
                    id: f.id,
                    title: f.title,
                    description: f.description,
                    status: f.status,
                    created_at: f.created_at || f.createdAt || new Date().toISOString(),
                    updated_at: f.updated_at || f.updatedAt || f.created_at || new Date().toISOString(),
                    vote_count: f.vote_count ?? f.votes_count ?? 0,
                    voted: Boolean(f.voted)
                }));
                return normalized;
            }
        }
        catch (fallbackErr) {
            return rejectWithValue(fallbackErr.response?.data?.message || fallbackErr.message);
        }
        return rejectWithValue(message);
    }
});
export const toggleFeatureVote = createAsyncThunk('community/toggleFeatureVote', async ({ feature_id }, { rejectWithValue }) => {
    try {
        const { data } = await axios.post('/community/features/vote', { feature_id });
        return { feature_id, voted: data.data.voted, vote_count: data.data.vote_count };
    }
    catch (err) {
        return rejectWithValue(err.response?.data?.message || err.message);
    }
});
export const submitFeatureComment = createAsyncThunk('community/submitFeatureComment', async ({ feature_id, content }, { rejectWithValue }) => {
    try {
        const { data } = await axios.post('/community/features/comment', { feature_id, content });
        return data.data;
    }
    catch (err) {
        return rejectWithValue(err.response?.data?.message || err.message);
    }
});
export const fetchFeatureComments = createAsyncThunk('community/fetchFeatureComments', async ({ feature_id }, { rejectWithValue }) => {
    try {
        const { data } = await axios.get(`/community/features/${feature_id}/comments`);
        return { feature_id, comments: data.data };
    }
    catch (err) {
        return rejectWithValue(err.response?.data?.message || err.message);
    }
});
// =============================
// Slice Definition
// =============================
const communitySlice = createSlice({
    name: 'community',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Tutor Posts
            .addCase(fetchTutorPosts.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchTutorPosts.fulfilled, (state, action) => {
            state.loading = false;
            state.posts = action.payload;
        })
            .addCase(fetchTutorPosts.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || 'Failed to load posts';
        })
            .addCase(createTutorPost.fulfilled, (state, action) => {
            state.posts.unshift(action.payload);
        })
            .addCase(togglePostLike.fulfilled, (state, action) => {
            const { post_id, liked, like_count } = action.payload;
            const post = state.posts.find(p => p.id === post_id);
            if (post) {
                post.liked = liked;
                post.like_count = like_count;
            }
        })
            .addCase(addPostComment.fulfilled, (state, action) => {
            const { post_id } = action.payload;
            if (!state.postComments[post_id]) {
                state.postComments[post_id] = [];
            }
            state.postComments[post_id].push(action.payload);
            // Update comment count on the post
            const post = state.posts.find(p => p.id === post_id);
            if (post) {
                post.comment_count += 1;
            }
        })
            .addCase(fetchPostComments.fulfilled, (state, action) => {
            const { post_id, comments } = action.payload;
            state.postComments[post_id] = comments;
        })
            // Features
            .addCase(fetchTutorFeatures.fulfilled, (state, action) => {
            state.features = action.payload;
        })
            .addCase(toggleFeatureVote.fulfilled, (state, action) => {
            const { feature_id, voted, vote_count } = action.payload;
            const feature = state.features.find(f => f.id === feature_id);
            if (feature) {
                feature.voted = voted;
                feature.vote_count = vote_count;
            }
        })
            .addCase(submitFeatureComment.fulfilled, (state, action) => {
            const { feature_id } = action.payload;
            if (!state.featureComments[feature_id]) {
                state.featureComments[feature_id] = [];
            }
            state.featureComments[feature_id].push(action.payload);
        })
            .addCase(fetchFeatureComments.fulfilled, (state, action) => {
            const { feature_id, comments } = action.payload;
            state.featureComments[feature_id] = comments;
        });
    }
});
export const { clearError } = communitySlice.actions;
export default communitySlice.reducer;
